import { useCallback, useEffect, useRef, useState, type DragEvent } from 'react';

import { CheckCircle2, Copy, Download, FileUp, XCircle } from 'lucide-react';

import type { ImportPreview, ImportResult, ImportRow } from '@/api/schema';
import { ApiError } from '@/api/client';
import { Alert } from '@/components/feedback/Alert';
import { useToast } from '@/components/feedback/Toast';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { Modal } from '@/components/ui/Modal';
import { Table, TableWrap, Td, Th, Tr } from '@/components/ui/Table';
import { cx } from '@/lib/cx';
import { NOTHING } from '@/lib/nothing';

import { importTemplateUrl, useImportCommit, useImportPreview } from '../api';
import styles from './ImportModal.module.css';

const MAX_BYTES = 5 * 1024 * 1024;

/**
 * Why this file cannot be sent, or null.
 *
 * Mirrors what the server refuses outright, so an obvious mistake is caught before a
 * five-megabyte round trip. The type check is deliberately loose: the same .csv file
 * arrives as `text/csv` from one browser, `application/vnd.ms-excel` from another and
 * an empty string from a third, so the extension is the only reliable signal and the
 * server does the real check anyway.
 */
export function csvProblem(file: File): string | null {
  if (!/\.csv$/i.test(file.name)) return 'Choose a .csv file.';
  if (file.size === 0) return 'That file is empty.';
  if (file.size > MAX_BYTES) return 'That file is over 5 MB.';
  return null;
}

type Stage =
  | { name: 'idle' }
  | { name: 'reading'; progress: number }
  | { name: 'preview'; result: ImportPreview }
  // Carries the count it started with, so the button keeps its label while it runs.
  | { name: 'importing'; progress: number; ready: number }
  | { name: 'done'; result: ImportResult };

/**
 * Bulk candidate creation from a spreadsheet.
 *
 * Two round trips on purpose. The first says what the file would do and writes
 * nothing; the second does it. The same file is uploaded twice because the preview
 * stores nothing on the server, which means what gets imported is what was checked,
 * rather than what a client claims was checked.
 *
 * All of the state lives in here rather than in the page. The modal's `onClose` comes
 * down as an inline arrow, and `useOverlay` re-runs its effect whenever that identity
 * changes, so a preview arriving in the parent's state would pull focus back to the
 * top of the dialog mid-read.
 */
export function ImportModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);

  const [stage, setStage] = useState<Stage>({ name: 'idle' });
  const [file, setFile] = useState<File | null>(null);
  /**
   * Which pick the answer on screen belongs to.
   *
   * Two files chosen in quick succession are two requests, and the second can land
   * first. Without this the preview shown could describe one file while `file` holds
   * another, and pressing Import would then upload something nobody had seen checked.
   */
  const pick = useRef(0);
  const [problem, setProblem] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const preview = useImportPreview();
  const commit = useImportCommit();

  const busy = stage.name === 'reading' || stage.name === 'importing';

  /**
   * The latest `busy`, readable from a callback that must not be rebuilt when it
   * changes. `close` has to stay stable for `useOverlay`'s sake, so it cannot close
   * over `busy` directly: it would keep whatever the value was when the dialog opened
   * and let a reader close the modal in the middle of writing rows.
   */
  const busyRef = useRef(busy);
  useEffect(() => {
    busyRef.current = busy;
  }, [busy]);

  /**
   * Stable, because `useOverlay` lists `onClose` in its effect deps: a new identity on
   * every render tears the effect down and re-runs it, which re-focuses the dialog and
   * overwrites the element to restore focus to. Every state change in here would
   * otherwise yank the keyboard back to the top mid-read.
   */
  const close = useCallback(() => {
    if (busyRef.current) return;
    pick.current += 1;
    setStage({ name: 'idle' });
    setFile(null);
    setProblem(null);
    setDragging(false);
    onClose();
  }, [onClose]);

  const choose = async (picked: File) => {
    // Refused outright while a commit is running: the rows are already being written
    // and there is nothing sensible to do with a second file until that finishes.
    if (busy) return;

    const mine = (pick.current += 1);
    const current = () => pick.current === mine;

    const refusal = csvProblem(picked);
    setFile(picked);
    setProblem(refusal);
    if (refusal) {
      setStage({ name: 'idle' });
      return;
    }

    setStage({ name: 'reading', progress: 0 });
    try {
      const result = await preview.mutateAsync({
        file: picked,
        onProgress: (fraction) => {
          if (current()) setStage({ name: 'reading', progress: fraction });
        },
      });
      if (!current()) return;
      setStage({ name: 'preview', result });
    } catch (error) {
      if (!current()) return;
      // A file the server cannot read at all comes back as one message about the
      // file, not as a list of rows. It belongs on the drop zone next to the file it
      // is about, which is why it is not a toast.
      setProblem(
        error instanceof ApiError ? error.message : 'That file could not be read. Try again.',
      );
      setStage({ name: 'idle' });
    }
  };

  const runImport = async () => {
    // Captured before the stage changes. Read from `stage` in the catch below it is
    // already 'importing' and carries no result, so a failed import would drop the
    // reader back to an empty dialog with their file gone.
    const checked = stage.name === 'preview' ? stage.result : null;
    if (!file || !checked) return;
    setStage({ name: 'importing', progress: 0, ready: checked.valid_count });
    try {
      const result = await commit.mutateAsync({
        file,
        onProgress: (fraction) => {
          setStage({ name: 'importing', progress: fraction, ready: checked.valid_count });
        },
      });
      setStage({ name: 'done', result });
      toast.success(
        result.created === 1
          ? '1 candidate imported.'
          : `${result.created} candidates imported.`,
      );
    } catch (error) {
      setProblem(
        error instanceof ApiError ? error.message : 'The import failed. Nothing was created.',
      );
      setStage({ name: 'preview', result: checked });
    }
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    if (busy) return;
    const dropped = event.dataTransfer.files[0];
    if (dropped) void choose(dropped);
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title="Import candidates"
      description="Upload a filled-in template. Every valid row becomes a candidate."
      size="lg"
      icon={<FileUp size={19} />}
      footer={<Footer stage={stage} busy={busy} onCancel={close} onImport={runImport} />}
    >
      {stage.name === 'done' ? (
        <Done result={stage.result} />
      ) : (
        <>
          <div className={styles.templateRow}>
            <span className={styles.templateText}>
              Not sure of the format? The template has the columns and two example rows.
            </span>
            {/* A real link, not a button: the response is a CSV body, which neither
                the JSON client nor the upload transport can fetch. */}
            <a className={styles.templateLink} href={importTemplateUrl()} download>
              <Download size={14} aria-hidden="true" />
              Download the template
            </a>
          </div>

          <div
            className={cx(
              styles.zone,
              dragging && styles.zoneActive,
              problem && styles.zoneError,
            )}
            onDragOver={(event) => {
              // Without this the browser navigates away to the dropped file and the
              // whole page is replaced by a CSV.
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => {
              setDragging(false);
            }}
            onDrop={onDrop}
          >
            <span className={styles.zoneIcon}>
              <FileUp size={20} aria-hidden="true" />
            </span>
            <p className={styles.zoneTitle}>
              {file ? file.name : 'Drop a CSV here, or choose a file'}
            </p>
            <p className={styles.zoneHint}>Up to 5 MB.</p>

            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() => {
                inputRef.current?.click();
              }}
            >
              {file ? 'Choose a different file' : 'Choose a file'}
            </Button>

            {/* The drop zone is not reachable by keyboard, so the input is what makes
                this usable without a mouse; the button above opens it. */}
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              className={styles.input}
              onChange={(event) => {
                const picked = event.target.files?.[0];
                // Cleared so choosing the same file twice fires again, which is what
                // happens after fixing it in a spreadsheet and re-picking it.
                event.target.value = '';
                if (picked) void choose(picked);
              }}
            />
          </div>

          {busy ? <Progress stage={stage} /> : null}

          {problem ? (
            <Alert tone="danger">
              <b>{problem}</b>
            </Alert>
          ) : null}

          {stage.name === 'preview' ? <Preview result={stage.result} /> : null}
        </>
      )}
    </Modal>
  );
}

function Progress({ stage }: { stage: { name: string; progress: number } }) {
  const percent = Math.round(stage.progress * 100);
  const label = stage.name === 'reading' ? 'Checking the file' : 'Importing';
  return (
    <div className={styles.progress}>
      <span className={styles.progressLabel}>
        {/* At 100% the bytes have arrived but the server is still working, and a bar
            that sits at 100% reads as stuck. */}
        {stage.progress >= 1 ? `${label}: finishing` : `${label}: ${percent}%`}
      </span>
      <div
        className={styles.track}
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
      >
        <div className={styles.bar} style={{ inlineSize: `${percent}%` }} />
      </div>
    </div>
  );
}

function Preview({ result }: { result: ImportPreview }) {
  const {
    valid_count: valid,
    invalid_count: invalid,
    duplicate_count: duplicates,
    file_duplicate_count: inFile,
  } = result;

  return (
    <div className={styles.preview}>
      {invalid > 0 ? (
        <Alert tone="warning">
          <b>
            {invalid === 1 ? '1 row cannot be imported' : `${invalid} rows cannot be imported`}
          </b>{' '}
          and will be skipped. {valid === 1 ? '1 row' : `${valid} rows`} will be created. Fix
          the file and upload it again if you would rather import everything.
        </Alert>
      ) : (
        <Alert tone="success">
          <b>{valid === 1 ? '1 row is ready' : `All ${valid} rows are ready`} to import.</b>
        </Alert>
      )}

      {duplicates > 0 ? (
        <Alert tone="info">
          <b>
            {duplicates === 1
              ? '1 row matches someone already in the register'
              : `${duplicates} rows match someone already in the register`}
          </b>{' '}
          on an email address or a phone number. They will still be imported: a shared phone is
          often a family, and a shared address is often an agent.
        </Alert>
      ) : null}

      {/* Said separately, because it is a different problem with a different fix: not
          "you may already have this person" but "you have typed this person twice". */}
      {inFile > 0 ? (
        <Alert tone="info">
          <b>
            {inFile === 1
              ? '1 row repeats an earlier row of this file'
              : `${inFile} rows repeat an earlier row of this file`}
          </b>{' '}
          on an email address or a phone number. Every one of them will be created.
        </Alert>
      ) : null}

      {result.truncated ? (
        <p className={styles.note}>
          Showing the first {result.rows.length} rows of {result.total_rows}. The counts above
          cover the whole file, and every valid row is imported.
        </p>
      ) : null}

      <TableWrap>
        <Table minWidth={560}>
          <thead>
            <Tr>
              <Th label="Line" />
              <Th label="Name" />
              <Th label="Status" />
              <Th label="What is wrong" />
            </Tr>
          </thead>
          <tbody>
            {result.rows.map((row) => (
              <PreviewRow key={row.line} row={row} />
            ))}
          </tbody>
        </Table>
      </TableWrap>
    </div>
  );
}

function PreviewRow({ row }: { row: ImportRow }) {
  const duplicate = row.duplicate_of.length > 0 || row.duplicate_of_line !== null;

  return (
    <Tr className={cx(!row.valid && styles.rowInvalid)}>
      <Td className={styles.line}>{row.line}</Td>
      <Td>{row.full_name || <span className={styles.none}>{NOTHING}</span>}</Td>
      <Td>
        {/* The word carries the meaning; the colour only agrees with it. */}
        {row.valid ? (
          <Chip tone={duplicate ? 'hold' : 'approved'} size="sm">
            {duplicate ? 'Duplicate' : 'Ready'}
          </Chip>
        ) : (
          <Chip tone="rejected" size="sm">
            Skipped
          </Chip>
        )}
      </Td>
      <Td>
        {row.errors.length > 0 ? (
          <ul className={styles.errors}>
            {row.errors.map((error) => (
              <li key={error.column}>
                <b>{error.column}</b>: {error.message}
              </li>
            ))}
          </ul>
        ) : duplicate ? (
          // Both, when both are true. Rendered as either/or, a row that repeats an
          // earlier line *and* matches the register showed only the first and hid the
          // fact that this person is already on file.
          <span className={styles.duplicate}>
            <Copy size={13} aria-hidden="true" />
            {[
              row.duplicate_of.length > 0 &&
                `Already in the register as ${row.duplicate_of.map((match) => match.reference).join(', ')}`,
              row.duplicate_of_line !== null &&
                `Same contact details as line ${row.duplicate_of_line}`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        ) : (
          <span className={styles.none}>{NOTHING}</span>
        )}
      </Td>
    </Tr>
  );
}

function Done({ result }: { result: ImportResult }) {
  return (
    <div className={styles.done}>
      <span className={cx(styles.doneIcon, result.created > 0 && styles.doneIconGood)}>
        {result.created > 0 ? <CheckCircle2 size={22} /> : <XCircle size={22} />}
      </span>
      <p className={styles.doneTitle}>
        {result.created === 1
          ? '1 candidate imported'
          : `${result.created} candidates imported`}
      </p>
      {result.skipped > 0 ? (
        <p className={styles.doneBody}>
          {result.skipped === 1 ? '1 row was skipped' : `${result.skipped} rows were skipped`}{' '}
          because of the problems listed below. Nothing was created for them.
        </p>
      ) : null}

      {result.truncated ? (
        <p className={styles.note}>
          Listing the first {result.skipped_rows.length} of {result.skipped} skipped rows.
        </p>
      ) : null}

      {result.skipped_rows.length > 0 ? (
        <TableWrap>
          <Table minWidth={480}>
            <thead>
              <Tr>
                <Th label="Line" />
                <Th label="Name" />
                <Th label="What is wrong" />
              </Tr>
            </thead>
            <tbody>
              {result.skipped_rows.map((row) => (
                <Tr key={row.line}>
                  <Td className={styles.line}>{row.line}</Td>
                  <Td>{row.full_name || <span className={styles.none}>{NOTHING}</span>}</Td>
                  <Td>
                    <ul className={styles.errors}>
                      {row.errors.map((error) => (
                        <li key={error.column}>
                          <b>{error.column}</b>: {error.message}
                        </li>
                      ))}
                    </ul>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      ) : null}
    </div>
  );
}

function Footer({
  stage,
  busy,
  onCancel,
  onImport,
}: {
  stage: Stage;
  busy: boolean;
  onCancel: () => void;
  onImport: () => Promise<void>;
}) {
  if (stage.name === 'done') {
    // "Done", not "Close": the dialog already has a Close in its corner, and two
    // controls with one name in one dialog is a coin toss for anyone driving it by
    // voice or reading it out.
    return <Button onClick={onCancel}>Done</Button>;
  }

  // Held across the importing stage. Read from the stage alone it drops to zero the
  // instant the button is pressed, so the control the reader just clicked relabels
  // itself "Import 0 candidates" while it spins.
  const ready =
    stage.name === 'preview'
      ? stage.result.valid_count
      : stage.name === 'importing'
        ? stage.ready
        : 0;

  return (
    <>
      <Button variant="ghost" onClick={onCancel} disabled={busy}>
        Cancel
      </Button>
      <Button
        onClick={() => {
          void onImport();
        }}
        loading={stage.name === 'importing'}
        disabled={stage.name !== 'preview' || ready === 0}
      >
        {ready === 1 ? 'Import 1 candidate' : `Import ${ready} candidates`}
      </Button>
    </>
  );
}
